// The public site's main menu (ui-conventions.md #158) — the mega menu every
// header carries, signed in or not, mirroring formulastudent.de's information
// architecture as the redesign prototype lays it out.
//
// Static config, typed. A link is either an in-app route (`to`) or a page on
// the TYPO3 site (`href`, drawn with an outbound mark). Only paths that the
// redirect manifest (tools/redirects) already knows are used here — no guessed
// deep links. As the public pages move into the portal (redesign batch 5:
// home, event, teams, results, documents), their entries switch from `href`
// to `to`; nothing else needs to change. (Done for home, event, teams,
// results, rules & documents and News, #162; Partners, #168; World ›
// Competitions, #170.)
//
// When the tenant's `cms` feature is on, the CMS menu is merged into this
// config by section (#160, owner ruling "merge by section":
// src/core/useSiteMenu.ts → mergeMenuSections below) — the app's own entries
// stay, the CMS pages join the section of the same name. The footer is the
// CMS footer alone (AF35). When the CMS refuses (feature off) or cannot be
// reached, this config alone is the menu and the footer — the site as it was.
import type { RouteLocationRaw } from 'vue-router'

export const WWW = 'https://www.formulastudent.de'

export interface SiteLink {
  label: string
  /** In-app route. */
  to?: RouteLocationRaw
  /** Outbound URL — opens in a new tab. */
  href?: string
  /** Shown with a lock: signing in may open it (CMS menu, AF14). */
  locked?: boolean
}

export interface SiteMenuGroup {
  heading: string
  links: SiteLink[]
}

export interface SiteMenuSection {
  key: string
  label: string
  groups: SiteMenuGroup[]
  /** The menu's highlight card (eyebrow, title, one line, target). */
  feature?: { eyebrow: string; title: string; text: string; link: SiteLink }
}

export const SITE_MENU: SiteMenuSection[] = [
  {
    key: 'event',
    label: 'Event',
    groups: [
      {
        heading: 'Formula Student Germany',
        links: [
          { label: 'About the event', to: { name: 'site-event' } },
          { label: 'Visitor information', href: `${WWW}/fsg` },
        ],
      },
      {
        heading: 'Results',
        links: [
          { label: 'FSG results', to: { name: 'site-results', params: { competition: 'fsg' } } },
          {
            label: 'Driverless Cup results',
            to: { name: 'site-results', params: { competition: 'dc' } },
          },
        ],
      },
      {
        heading: 'Rules & more',
        links: [
          { label: 'Rules & documents', to: { name: 'site-documents' } },
          { label: 'Rules FAQ', to: { name: 'rules-faqs' } },
        ],
      },
    ],
    feature: {
      eyebrow: 'The event',
      title: 'Hockenheimring',
      text: 'Dates, venue and the field',
      link: { label: 'About the event', to: { name: 'site-event' } },
    },
  },
  {
    key: 'teams',
    label: 'Teams',
    groups: [
      {
        heading: 'Teams',
        links: [
          { label: 'Registered teams', to: { name: 'site-teams' } },
          { label: 'Team archive', href: `${WWW}/teams` },
        ],
      },
    ],
  },
  {
    key: 'academy',
    label: 'Academy',
    groups: [{ heading: 'FSG Academy', links: [{ label: 'Academy', href: `${WWW}/academy` }] }],
  },
  {
    key: 'partners',
    label: 'Partners',
    groups: [
      // The partners page is the app's own (#168 AP6).
      { heading: 'Partners', links: [{ label: 'Partners of FSG', to: { name: 'site-partners' } }] },
    ],
  },
  {
    key: 'media',
    label: 'PR + Media',
    groups: [
      {
        heading: 'News & media',
        links: [
          // The News module (#162 AH6) lives in the site now.
          { label: 'Press & news', to: { name: 'news' } },
          { label: 'Media library', href: 'https://media.formulastudent.de' },
        ],
      },
      {
        heading: 'Press',
        links: [{ label: 'Press accreditation', to: { name: 'press-apply' } }],
      },
    ],
  },
  {
    key: 'world',
    label: 'World',
    groups: [
      {
        heading: 'Formula Student worldwide',
        links: [
          // World › Competitions is the app's own page (#170 AN10).
          { label: 'Competitions', to: { name: 'site-competitions' } },
          { label: 'World ranking list', href: 'https://fs-world.org' },
        ],
      },
    ],
  },
]

/** One column of the website footer. */
export interface SiteFooterColumn {
  heading: string
  links: SiteLink[]
}

/** The static footer: the fallback when the cms feature is off or the CMS
 *  footer is empty (#160 AF35 — the importer copied these columns into the
 *  CMS footer tree, where editors now keep them). */
export const SITE_FOOTER: SiteFooterColumn[] = [
  {
    heading: 'Event',
    links: [
      { label: 'About the event', to: { name: 'site-event' } },
      { label: 'Results', to: { name: 'site-results' } },
      { label: 'Rules & documents', to: { name: 'site-documents' } },
      { label: 'Teams', to: { name: 'site-teams' } },
      { label: 'News', to: { name: 'news' } },
    ],
  },
  {
    heading: 'Join',
    links: [
      { label: 'Academy', href: `${WWW}/academy` },
      { label: 'Partners', to: { name: 'site-partners' } },
      { label: 'Press accreditation', to: { name: 'press-apply' } },
      { label: 'Rules FAQ', to: { name: 'rules-faqs' } },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Imprint', href: `${WWW}/imprint` },
      { label: 'How FSG accounts work', to: { name: 'identity-info' } },
    ],
  },
]

// ── The CMS menu (#160 WP7) ─────────────────────────────────────────────────

/** A menu or footer entry as cmsMenu / cmsFooter return it (structural, so
 *  every nesting level of the generated type fits; the deepest has no
 *  children selected). */
export interface CmsMenuNode {
  locked: boolean
  page: {
    id: string
    kind: 'PAGE' | 'LINK' | 'SPACER' | 'SECTION' | 'APP'
    path: string | null
    title: string
    navTitle: string
    externalUrl: string | null
  }
  children?: readonly CmsMenuNode[]
}

/** Whether `url` is a path on this site (an in-app route), not an address
 *  elsewhere. Protocol-relative `//host` is elsewhere. */
export function isSitePath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//')
}

function labelOf(n: CmsMenuNode): string {
  return n.page.navTitle || n.page.title
}

/** The link a menu entry stands for, or null when it has nowhere to go (a
 *  spacer, a section without a page of its own). LINK pages open their
 *  external URL; one that points into the site stays an in-app route. */
export function cmsLink(n: CmsMenuNode): SiteLink | null {
  const label = labelOf(n)
  const lock = n.locked ? { locked: true } : {}
  if (n.page.kind === 'LINK' && n.page.externalUrl) {
    const url = n.page.externalUrl
    return isSitePath(url) ? { label, to: url, ...lock } : { label, href: url, ...lock }
  }
  if (n.page.kind === 'PAGE' && n.page.path) return { label, to: n.page.path, ...lock }
  return null
}

function linksBelow(n: CmsMenuNode): SiteLink[] {
  const out: SiteLink[] = []
  for (const c of n.children ?? []) {
    const l = cmsLink(c)
    if (l) out.push(l)
    out.push(...linksBelow(c))
  }
  return out
}

/** cmsMenu → the mega menu's sections. Each top-level entry is a section; a
 *  child with children of its own becomes a group (headed by it, its own page
 *  first), the other children collect into groups between spacers (TYPO3's
 *  menu column breaks). */
export function cmsMenuToSections(nodes: readonly CmsMenuNode[]): SiteMenuSection[] {
  const sections: SiteMenuSection[] = []
  for (const top of nodes) {
    if (top.page.kind === 'SPACER') continue
    const label = labelOf(top)
    const groups: SiteMenuGroup[] = []
    let loose: SiteMenuGroup = { heading: label, links: [] }
    const closeLoose = (heading: string) => {
      if (loose.links.length) groups.push(loose)
      loose = { heading, links: [] }
    }
    const self = cmsLink(top)
    if (self) loose.links.push(self)
    for (const c of top.children ?? []) {
      if (c.page.kind === 'SPACER') {
        closeLoose('')
        continue
      }
      if (c.children?.length) {
        // Its own group, in reading order: what came before closes first.
        closeLoose('')
        const own = cmsLink(c)
        const links = [...(own ? [own] : []), ...linksBelow(c)]
        if (links.length) groups.push({ heading: labelOf(c), links })
        continue
      }
      const l = cmsLink(c)
      if (l) loose.links.push(l)
    }
    closeLoose('')
    if (groups.length) sections.push({ key: top.page.id, label, groups })
  }
  return sections
}

/** cmsFooter → footer columns: a top-level entry with children is a column
 *  headed by it; top-level links without children share one column. */
export function cmsFooterToColumns(nodes: readonly CmsMenuNode[]): SiteFooterColumn[] {
  const cols: SiteFooterColumn[] = []
  const loose: SiteLink[] = []
  for (const n of nodes) {
    if (n.page.kind === 'SPACER') continue
    if (n.children?.length) {
      const own = cmsLink(n)
      const links = [...(own ? [own] : []), ...linksBelow(n)]
      if (links.length) cols.push({ heading: labelOf(n), links })
      continue
    }
    const l = cmsLink(n)
    if (l) loose.push(l)
  }
  if (loose.length) cols.push({ heading: '', links: loose })
  return cols
}

// ── merging the CMS into the static menu (#160, "merge by section") ─────────

/** Section and column names match case-insensitively, ignoring outer space. */
function sameName(a: string): string {
  return a.trim().toLowerCase()
}

/** The links of `groups` minus those whose label is already in `taken`
 *  (a CMS entry pointing at an app page the static menu already lists),
 *  dropping groups left empty. Adds the kept labels to `taken`. */
function freshGroups<G extends { links: SiteLink[] }>(
  groups: readonly G[],
  taken: Set<string>,
): G[] {
  const out: G[] = []
  for (const g of groups) {
    const links = g.links.filter((l) => {
      const k = sameName(l.label)
      if (taken.has(k)) return false
      taken.add(k)
      return true
    })
    if (links.length) out.push({ ...g, links })
  }
  return out
}

/**
 * The header menu: every static section (in SITE_MENU's order) with its own
 * entries — Teams, Results, Rules & documents… — followed by the CMS pages
 * filed under the CMS section of the same name; then the CMS sections with
 * no static counterpart (e.g. "Officials area" for officials). An entry whose
 * label the section already shows is not repeated; a section left with no
 * entries is hidden.
 */
export function mergeMenuSections(
  base: readonly SiteMenuSection[],
  cms: readonly SiteMenuSection[],
): SiteMenuSection[] {
  const byName = new Map(cms.map((c) => [sameName(c.label), c]))
  const used = new Set<string>()
  const out: SiteMenuSection[] = []
  const add = (s: SiteMenuSection, extra: readonly SiteMenuGroup[]) => {
    const taken = new Set<string>()
    const groups = [...freshGroups(s.groups, taken), ...freshGroups(extra, taken)]
    if (groups.length) out.push({ ...s, groups })
  }
  for (const s of base) {
    const name = sameName(s.label)
    const c = byName.get(name)
    if (c) used.add(name)
    add(s, c?.groups ?? [])
  }
  for (const c of cms) if (!used.has(sameName(c.label))) add(c, [])
  return out
}
